import { useState } from 'react';
import { apiWithOrg } from 'services/api';

export enum InvoiceStatus {
  NEW = 'new',
  SUBMITTED = 'submitted',
  CAPTURED = 'captured',
  CANCELED = 'canceled',
  ERROR = 'error',
  VOIDED = 'voided',
}

export enum PaymentStatusFilter {
  PAID = 'paid',
  UNPAID = 'unpaid',
}

export interface IInvoiceData {
  id: string;
  gateway_invoice_id: string;
  invoice_number: string;
  reference_number: string;
  ticket_alpha_num_id: string;
  original_amount: number;
  fee_amount: number;
  tax_amount: number;
  create_date: string;
  update_date: string;
  invoice_date: string;
  customer_first_name: string | null;
  customer_last_name: string | null;
  customer_phone: string | null;
  customer_email: string | null;
  entity_id: string;
  associate_id: string;
  associate_email: string;
  method: string;
  processor_id: string;
  payment_link_url: string;
  charge_id: string;
  channel: string | null;
  status: string;
  type: string;
  is_authorized: boolean;
  skip_authentication: boolean;
  sender_phone: string | null;
  sender_email: string | null;
  actions?: any;
}

export interface IInvoiceApiResponse {
  total_count: number;
  prev_key: string | null;
  next_key: string | null;
  items: IInvoiceData[];
}

export interface FetchInvoicesParams {
  start_date: string;
  end_date: string;
  search_string?: string;
  key?: string | null;
  paymentStatusFilter: PaymentStatusFilter;
  status_filter?: string[]; // Array of status values for filtering
  amount_1?: number; // Amount filter value (in dollars, will be converted for API)
  condition?: 'greater' | 'less' | 'equal'; // Amount comparison condition
}

export const isInvoicePaid = (status: string): boolean => {
  return status === InvoiceStatus.CAPTURED;
};

// Utility functions for status filtering at API level
const getAllInvoiceStatuses = (): string[] => {
  return Object.values(InvoiceStatus);
};

const getPaidStatuses = (): string[] => {
  return [InvoiceStatus.CAPTURED];
};

const getUnpaidStatuses = (): string[] => {
  const allStatuses = getAllInvoiceStatuses();
  const paidStatuses = getPaidStatuses();
  return allStatuses.filter(status => !paidStatuses.includes(status as InvoiceStatus));
};

export const getStatusesForFilter = (paymentStatusFilter: PaymentStatusFilter): string => {
  if (paymentStatusFilter === PaymentStatusFilter.PAID) {
    return getPaidStatuses().join(',');
  } else {
    return getUnpaidStatuses().join(',');
  }
};

// Amount filtering types for better type safety
export type AmountCondition = 'greater' | 'less' | 'equal';

// Helper function to validate amount filter parameters
export const validateAmountFilter = (amount?: number, condition?: AmountCondition): boolean => {
  if (amount !== undefined && condition === undefined) {
    console.warn('Amount filter requires both amount and condition parameters');
    return false;
  }
  if (condition !== undefined && amount === undefined) {
    console.warn('Amount condition requires amount parameter');
    return false;
  }
  return true;
};

const useInvoice = () => {
  const [invoices, setInvoices] = useState<IInvoiceData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [numResults, setNumResults] = useState<number>(0);
  const [prevKey, setPrevKey] = useState<string | null>(null);
  const [nextKey, setNextKey] = useState<string | null>(null);

  const fetchInvoices = async (params: FetchInvoicesParams, isActiveTab = true) => {
    if (!isActiveTab) return;
    
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        start_date: params.start_date,
        end_date: params.end_date,
        method: 'ACH,ach', // Filter for ACH payments only (handle both cases until normalization is complete)
        status: getStatusesForFilter(params.paymentStatusFilter), // Smart status filtering based on enum
        ...(params.search_string && { search_string: params.search_string }),
        ...(params.key && { key: params.key }),
        ...(params.status_filter && params.status_filter.length > 0 && { status: params.status_filter.join(',') }), // Override with explicit status filter if provided
        // Amount filtering - API expects dollars format (e.g., "50.00")
        ...(params.amount_1 && { amount_1: params.amount_1.toFixed(2) }),
        ...(params.condition && { condition: params.condition }),
      };

      const response = await apiWithOrg.get<IInvoiceApiResponse>('invoices', queryParams);
      
      // No client-side filtering needed - API returns filtered results
      const fetchedInvoices = response.data.items || [];
      
      setInvoices(fetchedInvoices);
      setNumResults(response.data.total_count);
      setPrevKey(response.data.prev_key || null);
      setNextKey(response.data.next_key || null);

    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch invoices');
      console.error('Error fetching invoices:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetInvoices = () => {
    setInvoices([]);
    setNumResults(0);
    setPrevKey(null);
    setNextKey(null);
    setError(null);
  };

  return {
    invoices,
    loading,
    error,
    numResults,
    prevKey,
    nextKey,
    fetchInvoices,
    resetInvoices,
  };
};

export default useInvoice;